import React from 'react';
import {useDispatch} from 'react-redux';

import * as analytics from 'analytics/Analytics';
import isArchived from 'archive/isArchived';
import {REQUESTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import deepCopy from 'lib/deepCopy';
import {BenchlingDispatch} from 'redux-stores/Store';
import openCreateRequestModal, {OpenCreateModalArgs} from 'requests/dashboard/openCreateRequestModal';
import {
  adaptRequestTasksGraphQLToUIRequestTasks,
  adaptSampleGroupsToPreparedUISampleGroups,
} from 'requests/request-view/RequestUIModelAdapters';
import {RequestDetailsRequestGraphQL} from 'requests/RequestConstants';
import EntityStore from 'stores/EntityStore';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import getSampleGroupIdsByRequestTaskId from 'workflows/samples/getSampleGroupIdsByRequestTaskId';
import prepareSampleGroupsGraphQLForTable from 'workflows/samples/prepareSampleGroupsGraphQLForTable';

interface CopyRequestButtonProps {
  disabled?: boolean;
  request: RequestDetailsRequestGraphQL;
}

interface ActionProps {
  onOpenCreateModal: (openModalArgs: OpenCreateModalArgs) => void;
}

export const stubbableFunctions = {
  mapDispatchToProps(dispatch: BenchlingDispatch): ActionProps {
    return {
      onOpenCreateModal: (openModalArgs: OpenCreateModalArgs) => {
        analytics.deprecatedTrackEvent(REQUESTS_ANALYTICS_ACTIONS.OPENED_COPY_REQUEST_MODAL);
        dispatch(openCreateRequestModal(openModalArgs));
      },
    };
  },
};

export default function CopyRequestButton(props: CopyRequestButtonProps): JSX.Element {
  const dispatch = useDispatch<BenchlingDispatch>();
  const {onOpenCreateModal} = stubbableFunctions.mapDispatchToProps(dispatch);

  return (
    <Tooltip
      title={
        isArchived(props.request.schema)
          ? 'Cannot copy this request because its schema is archived'
          : 'Create another'
      }
    >
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        className="u-mini-spacing-left"
        disabled={isArchived(props.request.schema)}
        icon="copy"
        onClick={() => {
          onOpenCreateModal({
            inCopyMode: true,
            schema: graphQLToREST('RequestSchema', props.request.schema),
            template: null,
            initialRequestor: EntityStore.currentUser(),
            initialAssignees: deepCopy(props.request.assigneesJson),
            initialProjectId: props.request.project.id,
            initialTags: deepCopy(props.request.tagsJson),
            initialRequestTasks: adaptRequestTasksGraphQLToUIRequestTasks(deepCopy(props.request.tasks)),
            initialSampleGroups: adaptSampleGroupsToPreparedUISampleGroups(
              prepareSampleGroupsGraphQLForTable(deepCopy(props.request.sampleGroups))
            ),
            initialSampleGroupCidsByRequestTaskCid: getSampleGroupIdsByRequestTaskId(props.request.tasks),
          });
        }}
        size={ButtonSize.SMALL}
      />
    </Tooltip>
  );
}
